/**
 * Shopify Integrations Service
 *
 * API client for the Shopify integration admin page.
 * Backend routes: /api/integrations/shopify/* (built by Offering Studio)
 */

import { authFetch } from '@/utils/auth-fetch';

export interface ShopifyConnectionStatus {
  connected: boolean;
  shopDomain: string | null;
  shopName: string | null;
  status: 'active' | 'disconnected' | 'syncing' | 'error';
  lastSyncAt: string | null;
  lastSyncStatus: 'success' | 'error' | null;
  lastSyncError: string | null;
  productCount: number;
  connectedAt: string | null;
  scopes: string[];
}

export interface ShopifySyncHistoryEntry {
  id: string;
  syncType: 'full' | 'incremental' | 'webhook';
  status: 'success' | 'error' | 'running';
  productsCreated: number;
  productsUpdated: number;
  productsDeleted: number;
  duration: number;
  error: string | null;
  startedAt: string;
  completedAt: string | null;
}

class ShopifyIntegrationsService {
  async getStatus(siteId: string): Promise<ShopifyConnectionStatus> {
    const response = await authFetch(`/api/integrations/shopify/status?siteId=${siteId}`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch Shopify status');
    }
    return response.json();
  }

  async getInstallUrl(siteId: string): Promise<{ installUrl: string }> {
    const response = await authFetch('/api/integrations/shopify/install', {
      method: 'POST',
      body: JSON.stringify({ siteId }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to start Shopify install');
    }
    return response.json();
  }

  async disconnect(siteId: string): Promise<void> {
    const response = await authFetch('/api/integrations/shopify/disconnect', {
      method: 'POST',
      body: JSON.stringify({ siteId }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to disconnect Shopify');
    }
  }

  async triggerSync(siteId: string): Promise<void> {
    const response = await authFetch('/api/integrations/shopify/sync', {
      method: 'POST',
      body: JSON.stringify({ siteId }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to trigger sync');
    }
  }

  async getSyncHistory(siteId: string): Promise<ShopifySyncHistoryEntry[]> {
    const response = await authFetch(`/api/integrations/shopify/sync-history?siteId=${siteId}`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch sync history');
    }
    const data = await response.json();
    return data.history;
  }
}

export const shopifyIntegrationsService = new ShopifyIntegrationsService();
